import React from "react";
import { MessageCircle, ExternalLink, Skull } from "lucide-react";

const DISCORD_URL = "https://discord.gg/ufZBwhGZFn";

export const AboutScreen = ({ onBack }) => (
  <div className="min-h-screen w-full flex items-center justify-center px-6 py-16" data-testid="about-screen">
    <div className="max-w-2xl w-full">
      <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-emergency/70 mb-4">A NOTE FROM THE FACILITY</p>
      <h2 className="horror-title text-5xl md:text-6xl mb-8">On <span className="accent">grief</span>, and machinery.</h2>
      <div className="narrative text-white/70 space-y-5 text-base md:text-lg">
        <p>
          <em>Dark Requiem</em> is a story about a boy who is asked to become an adult in a single night.
          It is about the things we inherit from our parents — kindness, ambition, the shape of our anger.
        </p>
        <p>
          Your choices matter, though not always in the ways you want them to. Some doors, once opened, stay open.
          Your sanity, composure, and corruption all decay differently. Certain revelations restore one and cost another.
          This is intentional.
        </p>
        <p>
          The whispers you hear are never the same twice — the facility itself decides what to say
          each time you listen too closely. Every scene reads itself aloud in a clean voice; the whispers
          come through in a lower, closer one. Adjust volume, music, and narration from the audio panel.
        </p>
      </div>

      {/* Credits */}
      <div className="mt-14 border border-white/10 p-8 bg-black/50" data-testid="credits-panel">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-emergency/70 mb-6 flex items-center gap-2">
          <Skull size={12} /> CREDITS
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-x-8 gap-y-3 text-sm md:text-base">
          <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/40 pt-1">GAME DEVELOPER</span>
          <span className="font-serif italic text-2xl text-white/95" data-testid="credit-developer">Danker</span>

          <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/40 pt-1">STORY WRITERS</span>
          <span className="font-serif italic text-2xl text-white/95" data-testid="credit-writers">Danker <span className="text-white/40">&amp;</span> Wheeze</span>
        </div>
      </div>

      {/* Discord */}
      <a
        href={DISCORD_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 block border border-emergency/40 hover:border-emergency bg-black/60 hover:bg-black/80 p-6 group transition-colors"
        data-testid="discord-link"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 border border-emergency/60 flex items-center justify-center">
            <MessageCircle size={18} className="text-emergency group-hover:animate-heartbeat" />
          </div>
          <div className="flex-1">
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-emergency/80 mb-1">// JOIN THE COMMUNITY</p>
            <p className="narrative text-white/90 text-base">
              Share endings, bug the developer, whisper theories — the Discord door is open.
            </p>
            <p className="font-mono text-xs text-white/50 mt-1 flex items-center gap-2">
              {DISCORD_URL} <ExternalLink size={10} />
            </p>
          </div>
        </div>
      </a>

      {/* Thank you */}
      <div className="mt-10 text-center" data-testid="thank-you">
        <p className="horror-title text-3xl md:text-4xl text-white/85 mb-3">
          Thank you for playing.
        </p>
        <p className="narrative text-white/50 italic text-sm md:text-base max-w-lg mx-auto">
          For every player who sat with Jack in the dark and listened — thank you.
          You gave a small, cursed story a heartbeat.
          Sleep well tonight. Or don&apos;t. We understand either way.
        </p>
        <p className="font-serif italic text-emergency/80 text-lg mt-6">
          — Danker &amp; Wheeze
        </p>
      </div>

      <button onClick={onBack} className="term-btn mt-12 max-w-xs" data-testid="about-back">
        BACK — to the gate
      </button>
    </div>
  </div>
);
